import { useStyles } from '@archipro-design/aria';
import * as S from './PaintPickerShell.style';
import type { MouseEvent, RefObject } from 'react';
import { useState } from 'react';
import { useEffect, useRef } from 'react';
import { useDebounceFn } from 'ahooks';
import type { Coordinates } from '../../type';
import { calculateColourAtPoint } from '../../util/paint-picker-util';
import { drawImageOnCanvas } from '../../util/draw-image-on-canvas';
import { usePaintPickerContext } from '~/modules/paint-picker/context/PaintPickerContext';
import {
    DEBOUNCE_COLOR_DETECT_DESKTOP,
    DEBOUNCE_VALUE,
    PALETTE_DEFAULT_LIMIT,
} from '@modules/paint-picker/config';
import { usedRandomPaintFromImage } from '@modules/paint-picker/util/get-random-paint-from-image';
import { useLogger } from '@archipro-website/logger/client';
import { useTracker } from '@archipro-website/tracker';

interface PaintPickerShellProps {
    imageRef: HTMLImageElement | null;
    imageSrc: string;
}

const PaintPickerShell = ({ imageRef, imageSrc }: PaintPickerShellProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [coordinates, setCoordinates] = useState<Coordinates>({ x: 0, y: 0 });
    const tracker = useTracker();

    const {
        state: { activeImageSrc, previewPaint, populateDefaults },
        matchRgbToReseneColour,
        onSelectPreview,
        updatePaintSelection,
        addPaintToSelection,
    } = usePaintPickerContext();
    const logger = useLogger();

    const { css } = useStyles();

    const { run: debounceCoordinate, cancel: stopDebounce } = useDebounceFn(
        (coordinates: Coordinates) => {
            setCoordinates(coordinates);
        },
        {
            wait: DEBOUNCE_COLOR_DETECT_DESKTOP,
            trailing: true,
        }
    );

    const { run: debounceDrawCanvas } = useDebounceFn(
        (
            canvasRef: RefObject<HTMLCanvasElement>,
            imageRef: HTMLImageElement
        ) => {
            (async () => {
                if (!canvasRef.current || !imageRef) {
                    return;
                }
                await drawImageOnCanvas(canvasRef.current, imageRef).catch(
                    (error) => {
                        logger.error('could not draw image canvas', error);
                    }
                );
            })();
        },
        {
            wait: DEBOUNCE_VALUE,
            maxWait: DEBOUNCE_VALUE,
            trailing: true,
        }
    );

    const getCoordinates = (event: MouseEvent<HTMLDivElement>): Coordinates => {
        const box = event.currentTarget.getBoundingClientRect();
        return {
            x: event.clientX - box.left,
            y: event.clientY - box.top,
        } as Coordinates;
    };

    const onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
        const coordinates = getCoordinates(event);
        debounceCoordinate(coordinates);
    };

    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        if (canvasRef.current) {
            const coordinates = getCoordinates(event);
            const color = calculateColourAtPoint(
                coordinates.x,
                coordinates.y,
                canvasRef.current
            );
            if (color) {
                const paintColour = matchRgbToReseneColour(color);
                if (paintColour) {
                    addPaintToSelection(paintColour);
                    return;
                }
            }
        }
        previewPaint && addPaintToSelection(previewPaint);
    };

    useEffect(() => {
        if (imageRef && canvasRef.current) {
            window.addEventListener('resize', () =>
                debounceDrawCanvas(canvasRef, imageRef)
            );
            return () =>
                window.removeEventListener('resize', () =>
                    debounceDrawCanvas(canvasRef, imageRef)
                );
        }
    }, [imageRef, debounceDrawCanvas]);

    useEffect(() => {
        (async () => {
            if (!imageRef || !canvasRef.current) {
                return;
            }

            try {
                await drawImageOnCanvas(canvasRef.current, imageRef);

                if (
                    populateDefaults &&
                    activeImageSrc &&
                    imageSrc === activeImageSrc
                ) {
                    const paints = usedRandomPaintFromImage(
                        canvasRef.current,
                        matchRgbToReseneColour,
                        PALETTE_DEFAULT_LIMIT,
                        activeImageSrc
                    );
                    updatePaintSelection(paints);
                    if (paints.length) {
                        onSelectPreview(paints[paints.length - 1] ?? null);
                    }
                }
            } catch (error) {
                logger.error('could not draw image canvas', error);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageSrc, activeImageSrc, populateDefaults, imageRef]);

    useEffect(() => {
        if (coordinates.x && coordinates.y && canvasRef?.current) {
            const color = calculateColourAtPoint(
                coordinates.x,
                coordinates.y,
                canvasRef.current
            );
            if (color) {
                const paintColour = matchRgbToReseneColour(color);
                if (paintColour && paintColour.id !== previewPaint?.id) {
                    onSelectPreview(paintColour);
                    tracker.log('Resene-Colour-Detected', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                        data: {
                            ExtraData: paintColour.title,
                        },
                    });
                }
            }
        }
    }, [
        coordinates,
        matchRgbToReseneColour,
        onSelectPreview,
        tracker,
        previewPaint,
    ]);

    return (
        <div
            className={`${css(S.PaintPickerShellStyle)} paint-picker-shell`}
            onMouseMove={(e) => onMouseMove(e)}
            onClick={onClick}
            onMouseLeave={() => {
                stopDebounce();
                setCoordinates({ x: 0, y: 0 });
            }}
        >
            <canvas ref={canvasRef} className={css(S.CanvasStyling)} />
        </div>
    );
};

// Will be used when we have a FF for the picker
// eslint-disable-next-line import/no-unused-modules
export default PaintPickerShell;
